import { Subject } from 'rxjs';
import { getCookie } from '@utiljs/cookie';
import request from '@utils/axiosWrapper';
import * as Ids from '@utils/ids';

// import * as pushMessageReceipt from './pushMessageReceipt';
import PushMessage, { RawMessage } from './PushMessage';

import poiInfoCache from '../../utils/poiInfoCache';

export enum PushClientConnectionResult {
    Open = 0,
    Error = 1,
}

/** 解析连接记录 */
export const parseConnectionRecords = (
    records: PushClientConnectionResult[],
): {
    /** 连接成功的次数。 */
    openCount: number;
    /**
     * 最新一次连接成功的尝试次数。
     * 如果最新一次的连接失败，则返回距离上一次连接成功的尝试次数。
     */
    tryCount: number;
} => {
    const openCount = records.filter(record => record === PushClientConnectionResult.Open).length;

    if (!records?.length) {
        return {
            openCount,
            tryCount: 0,
        };
    }

    let tryCount = 1;
    // 向前回溯尝试了多少次
    let i = records.length - 1;
    while (i > 0) {
        if (records[i - 1] !== PushClientConnectionResult.Error) {
            break;
        }
        tryCount += 1;
        i -= 1;
    }
    return {
        openCount,
        tryCount,
    };
};

export interface PushClientEventHandler {
    onConnectionOpen: (event: {
        pushToken: string;
        openCount: number;
        tryCount: number;
        records: PushClientConnectionResult[];
    }) => void;
    onConnectionError: (event: {
        pushToken: string;
        openCount: number;
        tryCount: number;
        records: PushClientConnectionResult[];
    }) => void;
    onConnectionClose: (event: {
        pushToken: string;
        openCount: number;
        records: PushClientConnectionResult[];
    }) => void;
}

/** 心跳检测 💓 服务端响应消息 */
const HEARTBEAT_MSG = 'HB';

/** 心跳检测 💓 时间间隔 */
const HEARTBEAT_CHECK_INTERVAL = 10 * 1000;

/** 断线重连尝试间隔 */
const RECONNECT_INTERVAL = 5 * 1000;

/**
 * Push 客户端
 * ### 主要逻辑：
 * 1. websocket 建连
 * 2. 心跳监测
 * 3. 浏览器 online/offline 事件监听
 * 4. 断线重连
 *
 * ### 心跳检测机制
 *
 * 客户端每间隔 N 秒向服务端发送一条固定格式的上报消息，服务端收到后会回复一条固定回执消息。
 * 如果在某次发送上报消息之前还没有收到前一次上报的回执，则认为心跳检测失败。
 */
class PushClient {
    /** 可供外部订阅 push 消息的 [RxJS Subject](https://rxjs.dev/guide/subject) */
    message$: Subject<PushMessage> = new Subject();

    /** * [WebSocket](https://developer.mozilla.org/en-US/docs/Web/API/WebSocket) 连接实例 */
    private wsConnection: WebSocket | null = null;

    /** push 服务地址列表，有多个地址时表示多域名容灾 */
    private urlList: string[] = [];

    /** 下次连接使用 urlList 中哪个链接，用于多域名容灾 */
    private nextUrlIndex = 0;

    /** 心跳检测 💓 定时器 */
    private heartbeatMonitorTimer: number | null = null;

    /** 发送的心跳 💓 消息是否收到服务端回应 */
    private heartbeatMessageAcked: boolean = true;

    /** 断线重连定时器 */
    private reconnectTimer: number | null = null;

    /** 连接记录，每一个元素代表这次连接是否成功 */
    private connectRecords: PushClientConnectionResult[] = [];

    private handlers?: Partial<PushClientEventHandler>;

    constructor(urlList: string[], handlers?: Partial<PushClientEventHandler>) {
        if (urlList.length === 0) {
            throw new Error('请提供至少1个push连接地址');
        }

        this.urlList = urlList;
        this.registerNetworkChangeEvent();
        this.connect();

        this.handlers = handlers;
    }

    /** 彻底熄火 */
    dispose() {
        this.disconnect();
        this.stopHeartbeatMonitor();
        this.unregisterNetworkChangeEvent();
        this.stopRetryConnect();
    }

    /** 建立 WebSocket 连接 */
    private connect() {
        if (this.wsConnection) {
            this.disconnect();
        }

        if (window.WebSocket) {
            this.wsConnection = new WebSocket(this.getNextConnectUrl());
            this.heartbeatMessageAcked = true;

            this.wsConnection.onmessage = this.wsOnMessageHandler;
            this.wsConnection.onopen = this.wsOnOpenHandler;
            this.wsConnection.onerror = this.wsOnErrorHandler;
            this.wsConnection.onclose = this.wsOnCloseHandler;
        }
    }

    /** 获取下一次连接使用的 URL */
    private getNextConnectUrl = () => {
        const url = this.urlList[this.nextUrlIndex];
        this.nextUrlIndex = (this.nextUrlIndex + 1) % this.urlList.length;

        return url;
    };

    /** websocket 新消息回调 */
    private wsOnMessageHandler = (event: MessageEvent) => {
        if (event === null || !event.data) {
            return;
        }

        if (event.data === HEARTBEAT_MSG) {
            this.heartbeatMessageAcked = true;
            return;
        }

        let rawData: { [index: string]: any } = {};

        try {
            rawData = JSON.parse(decodeURIComponent(event.data));
        } catch (e) {
            rawData = JSON.parse(event.data);
        }

        if (rawData.hasOwnProperty('code')) {
            const message = new PushMessage(rawData as RawMessage);

            /**
             *  判断消息是否属于当前账号，分两种情况：
             *  1. 推送给单门店，当前门店列表里能查到这个门店 ID
             *  2. 推送给账号(门店 ID设置为 -1)，但是本地只有 1 个门店（说明是 200 账号或者非 200 账号只绑定了1 个门店）这种场景后端会固定推送-1，需前端兼容
             */
            if (
                poiInfoCache.getPoiInfoById(Number(message.wmPoiId)) !== null ||
                (message.wmPoiId === '-1' && poiInfoCache.getPoiCount() === 1)
            ) {
                // 将消息透传给订阅方
                this.message$.next(message);
            }
        }
    };

    /** websocket 连接建立回调 */
    private wsOnOpenHandler = () => {
        this.connectRecords.push(PushClientConnectionResult.Open);

        this.restartHeartbeatMonitor();
        this.stopRetryConnect();

        const pushToken = getCookie('pushToken');
        request.postAsFormSubmit('/v2/common/pushToken/w/update', {
            uuid: getCookie('device_uuid'),
            acctId: Ids.getAcctId(),
            pushToken,
            type: 'connected',
            isMuti: 1,
        });

        if (this.handlers?.onConnectionOpen) {
            this.handlers.onConnectionOpen({
                pushToken,
                records: this.connectRecords,
                ...parseConnectionRecords(this.connectRecords),
            });
        }
    };

    /** websocket 连接错误回调 */
    private wsOnErrorHandler = () => {
        this.connectRecords.push(PushClientConnectionResult.Error);

        this.retryConnectLater();
        this.stopHeartbeatMonitor();

        if (this.handlers?.onConnectionError) {
            this.handlers.onConnectionError({
                pushToken: getCookie('pushToken'),
                records: this.connectRecords,
                ...parseConnectionRecords(this.connectRecords),
            });
        }
    };

    /** websocket 连接断开回调 */
    private wsOnCloseHandler = () => {
        this.retryConnectLater();
        this.stopHeartbeatMonitor();

        if (this.handlers?.onConnectionClose) {
            this.handlers.onConnectionClose({
                pushToken: getCookie('pushToken'),
                records: this.connectRecords,
                ...parseConnectionRecords(this.connectRecords),
            });
        }
    };

    /** 断开连接 */
    private disconnect() {
        if (this.wsConnection) {
            this.wsConnection.onopen = null;
            this.wsConnection.onmessage = null;
            this.wsConnection.onerror = null;
            this.wsConnection.onclose = null;
            this.wsConnection.close();
        }
        this.wsConnection = null;
    }

    /** 断线重连 */
    private retryConnectLater = () => {
        this.stopRetryConnect();

        this.reconnectTimer = window.setTimeout(() => {
            this.connect();
            // 上面的 connect 不一定能成功建连，所以继续延迟重连。重连成功后会clear定时器
            this.retryConnectLater();
        }, RECONNECT_INTERVAL);
    };

    /** 停止重连尝试 */
    private stopRetryConnect = () => {
        if (this.reconnectTimer !== null) {
            window.clearTimeout(this.reconnectTimer);
            this.reconnectTimer = null;
        }
    };

    /** 开启心跳监测 */
    private startHeartbeatMonitor = () => {
        this.heartbeatMonitorTimer = window.setInterval(() => {
            // 检测上一条心跳消息是否收到回执
            if (!this.heartbeatMessageAcked) {
                this.retryConnectLater();
                this.stopHeartbeatMonitor();
                return;
            }

            // 发送心跳 💓 消息
            if (this.wsConnection) {
                this.wsConnection.send(`~#HHHBBB#~${getCookie('pushToken')}`);
            }

            this.heartbeatMessageAcked = false;
        }, HEARTBEAT_CHECK_INTERVAL);
    };

    /** 关闭心跳监测 */
    private stopHeartbeatMonitor = () => {
        if (this.heartbeatMonitorTimer !== null) {
            window.clearInterval(this.heartbeatMonitorTimer);
        }
    };

    /** 重启心跳检测 */
    private restartHeartbeatMonitor() {
        this.stopHeartbeatMonitor();
        this.startHeartbeatMonitor();
    }

    /** 监听浏览器 online/offline 事件 */
    private registerNetworkChangeEvent() {
        if ('online' in navigator) {
            window.addEventListener('online', this.restartHeartbeatMonitor);
            window.addEventListener('offline', this.stopHeartbeatMonitor);
        }
    }

    /** 取消监听浏览器 online/offline 事件 */
    private unregisterNetworkChangeEvent() {
        window.removeEventListener('online', this.restartHeartbeatMonitor);
        window.removeEventListener('offline', this.stopHeartbeatMonitor);
    }
}

export default PushClient;
